
import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  inject,
  ChangeDetectionStrategy
} from '@angular/core';
import { FormsModule } from '@angular/forms';
import { ActivatedRoute, Router } from '@angular/router';
import { Subject, takeUntil } from 'rxjs';
import { ToastComponent } from '../../../components/toast/toast.component';
import { Empresa } from '../../../models/empresa.model';
import {
  Usuario,
  UsuarioCreatePayload,
  UsuarioUpdatePayload,
} from '../../../models/usuario.model';
import { EmpresaService } from '../../../services/empresa.service';
import { ModalRef, ModalService } from '../../../services/modal.service';
import { ToastService } from '../../../services/toast.service';
import { UsuarioService } from '../../../services/usuario.service';

interface UsuarioForm {
  nombre_completo: string;
  email: string;
  contra: string;
  telefono: string;
  permisosRaw: string;
}

@Component({
  selector: 'app-usuarios',
  imports: [FormsModule, ToastComponent],
  templateUrl: './usuarios.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  styleUrl: './usuarios.component.css',
})
export class UsuariosComponent implements OnInit, OnDestroy {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly empresaService = inject(EmpresaService);
  private readonly modalService = inject(ModalService);
  private readonly usuarioService = inject(UsuarioService);
  private readonly toastService = inject(ToastService);
  private readonly destroy$ = new Subject<void>();

  empresas: Empresa[] = [];
  usuarios: Usuario[] = [];

  selectedEmpresaId = 0;
  selectedEmpresaNombre = '';

  loadingEmpresas = false;
  loadingUsuarios = false;
  error = '';

  modalRef: ModalRef | null = null;
  editingUsuario: Usuario | null = null;
  deletingUsuario: Usuario | null = null;
  form: UsuarioForm = this.getDefaultForm();

  ngOnInit(): void {
    this.loadEmpresas();

    this.route.paramMap.pipe(takeUntil(this.destroy$)).subscribe((params) => {
      const empresaId = Number(params.get('empresaId'));
      if (Number.isFinite(empresaId) && empresaId > 0) {
        this.selectedEmpresaId = empresaId;
      }
    });
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  onEmpresaChange(): void {
    if (!this.selectedEmpresaId) {
      this.usuarios = [];
      this.selectedEmpresaNombre = '';
      this.error = '';
      return;
    }

    const empresa = this.empresas.find(
      (item) => item.id === this.selectedEmpresaId,
    );
    this.selectedEmpresaNombre = empresa?.nombre || '';
    this.loadUsuarios();
  }

  openCreateModal(modalTemplate: TemplateRef<unknown>): void {
    if (!this.selectedEmpresaId) {
      this.toastService.show({
        text: 'Selecciona una empresa antes de crear usuarios',
        classname: 'bg-warning text-dark',
        delay: 3000,
      });
      return;
    }

    this.editingUsuario = null;
    this.form = this.getDefaultForm();
    this.openModal(modalTemplate);
  }

  openEditModal(usuario: Usuario, modalTemplate: TemplateRef<unknown>): void {
    this.editingUsuario = usuario;
    this.form = {
      nombre_completo: usuario.nombre_completo || '',
      email: usuario.email || '',
      contra: '',
      telefono: usuario.telefono || '',
      permisosRaw: (usuario.permisos || []).join(', '),
    };
    this.openModal(modalTemplate);
  }

  closeModal(): void {
    this.modalRef?.dismiss('manual-close');
  }

  saveUsuario(modal: ModalRef): void {
    if (!this.selectedEmpresaId) {
      return;
    }

    if (!this.form.nombre_completo.trim() || !this.form.email.trim()) {
      this.toastService.show({
        text: 'Nombre y email son obligatorios',
        classname: 'bg-danger text-light',
        delay: 3500,
      });
      return;
    }

    if (!this.editingUsuario && !this.form.contra.trim()) {
      this.toastService.show({
        text: 'La contraseña es obligatoria para crear un usuario',
        classname: 'bg-danger text-light',
        delay: 3500,
      });
      return;
    }

    const permisos = this.parsePermisos(this.form.permisosRaw);

    if (this.editingUsuario) {
      const updatePayload = this.buildUpdatePayload(permisos);
      const changedPayload = this.getChangedUpdatePayload(
        this.editingUsuario,
        updatePayload,
      );

      if (Object.keys(changedPayload).length === 0) {
        this.toastService.show({
          text: 'No hay cambios para guardar',
          classname: 'bg-info text-dark',
          delay: 2500,
        });
        return;
      }

      this.usuarioService
        .updateByEmpresa(
          this.selectedEmpresaId,
          this.editingUsuario.id,
          changedPayload,
        )
        .pipe(takeUntil(this.destroy$))
        .subscribe({
          next: () => {
            this.toastService.show({
              text: 'Usuario actualizado exitosamente',
              classname: 'bg-success text-light',
              delay: 3000,
            });
            modal.close('saved');
            this.loadUsuarios();
          },
          error: (error) => {
            this.toastService.show({
              text: error?.message || 'No se pudo guardar el usuario',
              classname: 'bg-danger text-light',
              delay: 4500,
            });
          },
        });

      return;
    }

    const request$ = this.usuarioService.createByEmpresa(
      this.selectedEmpresaId,
      this.buildCreatePayload(permisos),
    );

    request$.pipe(takeUntil(this.destroy$)).subscribe({
      next: () => {
        this.toastService.show({
          text: this.editingUsuario
            ? 'Usuario actualizado exitosamente'
            : 'Usuario creado exitosamente',
          classname: 'bg-success text-light',
          delay: 3000,
        });
        modal.close('saved');
        this.loadUsuarios();
      },
      error: (error) => {
        this.toastService.show({
          text: error?.message || 'No se pudo guardar el usuario',
          classname: 'bg-danger text-light',
          delay: 4500,
        });
      },
    });
  }

  openDeleteModal(usuario: Usuario, modalTemplate: TemplateRef<unknown>): void {
    if (!this.selectedEmpresaId) {
      return;
    }

    this.deletingUsuario = usuario;
    this.openModal(modalTemplate, 'md');
  }

  removeUsuario(modal: ModalRef): void {
    if (!this.selectedEmpresaId) {
      return;
    }

    const usuario = this.deletingUsuario;
    if (!usuario) {
      modal.dismiss('missing-user');
      return;
    }

    this.usuarioService
      .deleteByEmpresa(this.selectedEmpresaId, usuario.id)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: () => {
          this.toastService.show({
            text: 'Usuario eliminado exitosamente',
            classname: 'bg-success text-light',
            delay: 3000,
          });
          modal.close('deleted');
          this.loadUsuarios();
        },
        error: (error) => {
          this.toastService.show({
            text: error?.message || 'No se pudo eliminar el usuario',
            classname: 'bg-danger text-light',
            delay: 4500,
          });
          modal.dismiss('delete-error');
        },
      });
  }

  private openModal(
    template: TemplateRef<unknown>,
    size: 'md' | 'xl' = 'md',
  ): void {
    const ref = this.modalService.open(template, {
      size,
      backdrop: true,
    });

    this.modalRef = ref;

    ref.result.finally(() => {
      if (this.modalRef !== ref) {
        return;
      }

      this.modalRef = null;
      this.editingUsuario = null;
      this.deletingUsuario = null;
    });
  }

  isDeleted(usuario: Usuario): boolean {
    return Boolean(usuario.deleted_at);
  }

  goToEmpresas(): void {
    this.router.navigate(['/dashboard/empresas']);
  }

  private loadEmpresas(): void {
    this.loadingEmpresas = true;

    this.empresaService
      .getEmpresas()
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (empresas) => {
          this.empresas = empresas;
          this.loadingEmpresas = false;

          if (!this.selectedEmpresaId && this.empresas.length > 0) {
            this.selectedEmpresaId = this.empresas[0].id;
          }

          this.onEmpresaChange();
        },
        error: (error) => {
          this.error = error?.message || 'No se pudieron cargar las empresas';
          this.loadingEmpresas = false;
        },
      });
  }

  private loadUsuarios(): void {
    this.loadingUsuarios = true;
    this.error = '';

    this.usuarioService
      .getByEmpresa(this.selectedEmpresaId)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (usuarios) => {
          this.usuarios = usuarios;
          this.loadingUsuarios = false;
        },
        error: (error) => {
          this.error = error?.message || 'No se pudieron cargar los usuarios';
          this.loadingUsuarios = false;
        },
      });
  }

  private parsePermisos(raw: string): string[] {
    const values = raw
      .split(',')
      .map((item) => item.trim().toUpperCase())
      .filter((item) => item.length > 0);

    return values.length > 0 ? values : ['VENDEDOR'];
  }

  private buildCreatePayload(permisos: string[]): UsuarioCreatePayload {
    return {
      nombre_completo: this.form.nombre_completo.trim(),
      email: this.form.email.trim().toLowerCase(),
      contra: this.form.contra.trim(),
      telefono: this.form.telefono.trim() || undefined,
      permisos,
    };
  }

  private buildUpdatePayload(permisos: string[]): UsuarioUpdatePayload {
    const payload: UsuarioUpdatePayload = {
      nombre_completo: this.form.nombre_completo.trim(),
      email: this.form.email.trim().toLowerCase(),
      telefono: this.form.telefono.trim() || undefined,
      permisos,
    };

    if (this.form.contra.trim()) {
      payload.contra = this.form.contra.trim();
    }

    return payload;
  }

  private getChangedUpdatePayload(
    usuario: Usuario,
    payload: UsuarioUpdatePayload,
  ): UsuarioUpdatePayload {
    const original = this.buildOriginalUpdateBaseline(usuario);
    const changedPayload: UsuarioUpdatePayload = {};

    if (payload.nombre_completo !== original.nombre_completo) {
      changedPayload.nombre_completo = payload.nombre_completo;
    }

    if (payload.email !== original.email) {
      changedPayload.email = payload.email;
    }

    if (payload.telefono !== original.telefono) {
      changedPayload.telefono = payload.telefono;
    }

    const payloadPermisos = this.normalizePermisos(payload.permisos);
    const originalPermisos = this.normalizePermisos(original.permisos);
    if (JSON.stringify(payloadPermisos) !== JSON.stringify(originalPermisos)) {
      changedPayload.permisos = payload.permisos;
    }

    if (payload.contra) {
      changedPayload.contra = payload.contra;
    }

    return changedPayload;
  }

  private buildOriginalUpdateBaseline(usuario: Usuario): UsuarioUpdatePayload {
    return {
      nombre_completo: (usuario.nombre_completo || '').trim(),
      email: (usuario.email || '').trim().toLowerCase(),
      telefono: (usuario.telefono || '').trim() || undefined,
      permisos: this.normalizePermisos(usuario.permisos),
    };
  }

  private normalizePermisos(permisos?: string[]): string[] {
    const values = Array.isArray(permisos)
      ? permisos
          .map((item) => (item || '').trim().toUpperCase())
          .filter((item) => item.length > 0)
      : [];

    return [...new Set(values)].sort();
  }

  private getDefaultForm(): UsuarioForm {
    return {
      nombre_completo: '',
      email: '',
      contra: '',
      telefono: '',
      permisosRaw: 'VENDEDOR',
    };
  }
}
